/**
 * The annual UMA value (GET/POST /compliance/uma-values). Visible and writable only to the designated
 * compliance officer and super_admins; the server answers 403 to everyone else.
 *
 * A value that differs by more than 10% from the one it replaces is answered 409
 * `uma_large_change_needs_confirmation` until the same entry is sent with `confirm_large_change`.
 */
import type { AxiosInstance } from 'axios';
import authService from './auth.service';

export interface UmaValue {
  id: string;
  /** YYYY-MM-DD */
  effective_from: string;
  /** Pesos, two decimals, as sent by the server. */
  daily_value_mxn: string;
  dof_publication_date: string;
  applies_to_year: number | null;
  confirmed_large_change: boolean;
  entered_by_name: string | null;
  entered_at: string;
}

export interface UmaOverview {
  today: string;
  in_force: UmaValue | null;
  /** The day the screen proposes for the next entry: February 1. */
  proposed_effective_from: string;
  history: UmaValue[];
}

export interface UmaEntry {
  daily_value_mxn: string;
  effective_from: string;
  dof_publication_date: string;
  applies_to_year: number | null;
  confirm_large_change: boolean;
}

/** What the 409 carries so the screen can ask for the confirmation. */
export interface LargeChange {
  current_value: string;
  difference_percent: string;
}

const BASE = '/api/v1/compliance/uma-values';

class UmaValuesService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async overview(): Promise<UmaOverview> {
    return (await this.api.get<UmaOverview>(BASE)).data;
  }

  async enter(entry: UmaEntry): Promise<UmaValue> {
    return (await this.api.post<UmaValue>(BASE, entry)).data;
  }
}

export default new UmaValuesService();
